import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Title,
  Text,
  Card,
  Group,
  Stack,
  Avatar,
  Button,
  Loader,
  Center,
  Alert,
  Divider,
} from '@mantine/core';
import {
  IconArrowLeft,
  IconAlertCircle,
  IconBan,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../context/AuthContext';
import { moderationService, type BlockedUser } from '../../services/moderation';

export default function BlockedUsers() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [unblockingId, setUnblockingId] = useState<string | null>(null);

  const { data: blockedUsers = [], isLoading } = useQuery({
    queryKey: ['blocked-users'],
    queryFn: () => moderationService.getBlockedUsersWithDetails(),
    staleTime: 60_000,
  });

  const unblockMutation = useMutation({
    mutationFn: ({ userId }: { userId: string; blockedUsername: string }) =>
      moderationService.unblockUser(userId),
    onMutate: ({ userId }) => setUnblockingId(userId),
    onSuccess: (_data, { userId, blockedUsername }) => {
      queryClient.setQueryData(['blocked-users'], (old: BlockedUser[] | undefined) =>
        (old ?? []).filter((u) => u.id !== userId)
      );
      notifications.show({
        title: 'Unblocked',
        message: `@${blockedUsername} has been unblocked`,
        color: 'green',
      });
    },
    onError: () => {
      notifications.show({
        title: 'Error',
        message: 'Failed to unblock user',
        color: 'red',
      });
    },
    onSettled: () => setUnblockingId(null),
  });

  if (!user) {
    return (
      <Container size="sm" pt="md" pb="xl">
        <Alert icon={<IconAlertCircle size={16} />} color="red" title="Not logged in">
          You must be logged in to view blocked users.
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="sm" pt="md" pb="xl">
      <Button
        variant="subtle"
        size="compact-sm"
        leftSection={<IconArrowLeft size={14} />}
        onClick={() => navigate('/settings')}
        mb="xs"
      >
        Back to Settings
      </Button>

      <Title order={2} mb="lg">
        Blocked Users
      </Title>

      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Group gap="xs" mb="md">
          <IconBan size={20} />
          <Title order={4}>Manage Blocked Users</Title>
        </Group>

        {isLoading ? (
          <Center py="lg">
            <Loader size="sm" />
          </Center>
        ) : blockedUsers.length === 0 ? (
          <Text c="dimmed" size="sm" ta="center" py="lg">
            You haven't blocked anyone.
          </Text>
        ) : (
          <Stack gap="xs">
            {blockedUsers.map((blockedUser) => (
              <div key={blockedUser.id}>
                <Group justify="space-between" py="xs">
                  <Group
                    gap="sm"
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/users/${blockedUser.username}`)}
                  >
                    <Avatar
                      src={blockedUser.profileImageUrl}
                      alt={blockedUser.username}
                      size={40}
                      radius="xl"
                    />
                    <div>
                      <Text size="sm" fw={500}>
                        @{blockedUser.username}
                      </Text>
                      <Text size="xs" c="dimmed">
                        Blocked {new Date(blockedUser.blockedAt).toLocaleDateString()}
                      </Text>
                    </div>
                  </Group>

                  <Button
                    variant="outline"
                    color="red"
                    size="xs"
                    onClick={() => unblockMutation.mutate({ userId: blockedUser.id, blockedUsername: blockedUser.username })}
                    loading={unblockingId === blockedUser.id}
                  >
                    Unblock
                  </Button>
                </Group>
                <Divider />
              </div>
            ))}
          </Stack>
        )}
      </Card>
    </Container>
  );
}
